import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button, Card, CardBody, CardHeader, Chip, Spinner } from "@heroui/react";
import {
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  PlusIcon,
  ArrowsRightLeftIcon,
  MinusCircleIcon,
  XMarkIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { ListCommanWay, CreateCommanWay } from "API/commonApi";
import Select from "react-select";
import UseToast from "utils/AfToast";

// Predefined item names for dropdown
const STOCK_ITEM_OPTIONS = [
  { value: "cube-6", label: "Cube-6" },
  { value: "cube-8", label: "Cube-8" },
  { value: "cube-10", label: "Cube-10" },
  { value: "flyer-s", label: "Flyer-S" },
  { value: "flyer-m", label: "Flyer-M" },
  { value: "flyer-l", label: "Flyer-L" },
  { value: "pouch-small", label: "Pouch-Small" },
  { value: "pouch-medium", label: "Pouch-Medium" },
  { value: "pouch-large", label: "Pouch-Large" },
  { value: "tape", label: "Tape" },
  { value: "sticker-roll", label: "Sticker Roll" },
  { value: "other", label: "Other" },
];

const CATEGORY_OPTIONS = [
  { value: "packaging", label: "Packaging" },
  { value: "stationery", label: "Stationery" },
  { value: "equipment", label: "Equipment" },
  { value: "other", label: "Other" },
];

const StockReport: React.FC = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [sortStat, setSortStat] = useState<any>({});
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [useStockModalOpen, setUseStockModalOpen] = useState(false);

  // Add stock form
  const [addForm, setAddForm] = useState({
    name: null as any,
    desc: "",
    category: null as any,
    qty: "",
    subbranch: null as any,
    boughtBy: null as any,
    note: "",
  });

  // Transfer stock form
  const [transferForm, setTransferForm] = useState({
    name: null as any,
    qty: "",
    fromBranch: null as any,
    toBranch: null as any,
    note: "transfer",
  });

  // Use stock form
  const [useForm, setUseForm] = useState({
    name: null as any,
    qty: "",
    subbranch: null as any,
    note: "",
  });

  const tableRef = useRef<HTMLTableElement>(null);

  // Fetch branches
  useQuery(
    ["SubbranchListApi"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          const opt = data.data.data.map((p: any) => ({
            value: p.id,
            label: p.branch_name,
          }));
          setBranchOptions(opt);
        }
      },
    }
  );

  // Fetch users for boughtBy
  useQuery(
    ["UserListApi"],
    () => ListCommanWay("/admin/user/list", {}, { paginate: 1000 }),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          const opt = data.data.data.map((p: any) => ({
            value: p.id,
            label: p.name || p.username,
          }));
          setUserOptions(opt);
        }
      },
    }
  );

  // Fetch inventory list
  const { isFetching, isLoading, refetch } = useQuery(
    ["inventory_list", selectedBranch],
    () =>
      ListCommanWay(
        "/admin/inventory/list",
        selectedBranch?.value ? { subbranch: selectedBranch.value } : {},
        { paginate: 1000, include: ["_subbranch", "_boughtBy", "_addedBy"] }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status === "SUCCESS" && data.data) {
          setTableData(data.data.data || []);
        } else {
          setTableData([]);
        }
      },
      onError() {
        setTableData([]);
      },
    }
  );

  // Create inventory mutation
  const { mutate: createInventory, isLoading: isCreating } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/inventory/create", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Stock added successfully!", "success");
          setAddModalOpen(false);
          resetAddForm();
          refetch();
        } else {
          UseToast(data.message || "Failed to add stock", "error");
        }
      },
      onError: () => {
        UseToast("Failed to add stock", "error");
      },
    }
  );

  // Transfer stock mutation (creates 2 entries)
  const { mutate: transferStock, isLoading: isTransferring } = useMutation(
    async (data: any) => {
      // Create negative entry for source branch
      const sourceEntry = await CreateCommanWay({
        url: "/admin/inventory/create",
        data: {
          name: data.name,
          desc: data.desc || "",
          category: data.category || "packaging",
          qty: -Math.abs(data.qty),
          subbranch: data.fromBranch,
          note: "transfer",
        },
      });

      // Create positive entry for destination branch
      const destEntry = await CreateCommanWay({
        url: "/admin/inventory/create",
        data: {
          name: data.name,
          desc: data.desc || "",
          category: data.category || "packaging",
          qty: Math.abs(data.qty),
          subbranch: data.toBranch,
          note: "transfer",
        },
      });

      return { sourceEntry, destEntry };
    },
    {
      onSuccess: (data: any) => {
        if (data.sourceEntry?.status === "SUCCESS" && data.destEntry?.status === "SUCCESS") {
          UseToast("Stock transferred successfully!", "success");
          setTransferModalOpen(false);
          resetTransferForm();
          refetch();
        } else {
          UseToast("Failed to transfer stock", "error");
        }
      },
      onError: () => {
        UseToast("Failed to transfer stock", "error");
      },
    }
  );

  // Use stock mutation (subtracts qty)
  const { mutate: useStock, isLoading: isUsingStock } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/inventory/create",
        data: {
          ...data,
          qty: -Math.abs(data.qty),
        },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Stock usage recorded successfully!", "success");
          setUseStockModalOpen(false);
          resetUseForm();
          refetch();
        } else {
          UseToast(data.message || "Failed to record stock usage", "error");
        }
      },
      onError: () => {
        UseToast("Failed to record stock usage", "error");
      },
    }
  );

  const resetAddForm = () => {
    setAddForm({
      name: null,
      desc: "",
      category: null,
      qty: "",
      subbranch: null,
      boughtBy: null,
      note: "",
    });
  };

  const resetTransferForm = () => {
    setTransferForm({
      name: null,
      qty: "",
      fromBranch: null,
      toBranch: null,
      note: "transfer",
    });
  };

  const resetUseForm = () => {
    setUseForm({
      name: null,
      qty: "",
      subbranch: null,
      note: "",
    });
  };

  const handleAddStock = () => {
    if (!addForm.name?.value) {
      UseToast("Please select an item name", "error");
      return;
    }
    if (addForm.name.value === "other" && !addForm.desc.trim()) {
      UseToast("Please enter item description", "error");
      return;
    }
    if (!addForm.qty || isNaN(Number(addForm.qty)) || Number(addForm.qty) <= 0) {
      UseToast("Please enter a valid quantity", "error");
      return;
    }
    if (!addForm.subbranch?.value) {
      UseToast("Please select a branch", "error");
      return;
    }

    createInventory({
      name: addForm.name.value === "other" ? addForm.desc : addForm.name.value,
      desc: addForm.desc,
      category: addForm.category?.value || "packaging",
      qty: Number(addForm.qty),
      subbranch: addForm.subbranch.value,
      boughtBy: addForm.boughtBy?.value || null,
      note: addForm.note,
    });
  };

  const handleTransferStock = () => {
    if (!transferForm.name?.value) {
      UseToast("Please select an item name", "error");
      return;
    }
    if (!transferForm.qty || isNaN(Number(transferForm.qty)) || Number(transferForm.qty) <= 0) {
      UseToast("Please enter a valid quantity", "error");
      return;
    }
    if (!transferForm.fromBranch?.value) {
      UseToast("Please select source branch", "error");
      return;
    }
    if (!transferForm.toBranch?.value) {
      UseToast("Please select destination branch", "error");
      return;
    }
    if (transferForm.fromBranch.value === transferForm.toBranch.value) {
      UseToast("Source and destination branch cannot be same", "error");
      return;
    }

    transferStock({
      name: transferForm.name.value,
      qty: Number(transferForm.qty),
      fromBranch: transferForm.fromBranch.value,
      toBranch: transferForm.toBranch.value,
    });
  };

  const handleUseStock = () => {
    if (!useForm.name?.value) {
      UseToast("Please select an item name", "error");
      return;
    }
    if (!useForm.qty || isNaN(Number(useForm.qty)) || Number(useForm.qty) <= 0) {
      UseToast("Please enter a valid quantity", "error");
      return;
    }
    if (!useForm.subbranch?.value) {
      UseToast("Please select a branch", "error");
      return;
    }

    useStock({
      name: useForm.name.value,
      desc: "",
      category: "packaging",
      qty: Number(useForm.qty),
      subbranch: useForm.subbranch.value,
      note: useForm.note || "used",
    });
  };

  const handleSort = (key: string) => {
    let data = [...tableData];
    let order = sortStat[key] === "asc" ? "desc" : "asc";

    const sortedData = data.sort((a, b) => {
      const valueA = key.split(".").reduce((obj, k) => obj?.[k], a) || "";
      const valueB = key.split(".").reduce((obj, k) => obj?.[k], b) || "";
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortStat({ [key]: order });
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key: string) => {
    const iconClass = "h-4 w-4 inline-block cursor-pointer ml-1";
    switch (sortStat[key]) {
      case "asc":
        return <ArrowUpIcon className={iconClass} onClick={() => handleSort(key)} />;
      case "desc":
        return <ArrowDownIcon className={iconClass} onClick={() => handleSort(key)} />;
      default:
        return <ArrowsUpDownIcon className={iconClass} onClick={() => handleSort(key)} />;
    }
  };

  const exportToExcel = () => {
    if (!tableRef.current) return;
    const tableClone = tableRef.current.cloneNode(true) as HTMLTableElement;
    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Stock Report");
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Stock_Report_${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  // Calculate stock summary by item name
  const stockSummary = React.useMemo(() => {
    const summary: Record<string, number> = {};
    tableData.forEach((item) => {
      const name = item.name || "Unknown";
      summary[name] = (summary[name] || 0) + (Number(item.qty) || 0);
    });
    return summary;
  }, [tableData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-inter">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-2 rounded-lg">
              <CubeIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Stock Report</h1>
              <p className="text-slate-300 text-sm">Inventory Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="success"
              variant="solid"
              size="sm"
              startContent={<PlusIcon className="h-4 w-4" />}
              onClick={() => setAddModalOpen(true)}
            >
              Add Stock
            </Button>
            <Button
              color="primary"
              variant="solid"
              size="sm"
              startContent={<ArrowsRightLeftIcon className="h-4 w-4" />}
              onClick={() => setTransferModalOpen(true)}
            >
              Transfer
            </Button>
            <Button
              color="warning"
              variant="solid"
              size="sm"
              startContent={<MinusCircleIcon className="h-4 w-4" />}
              onClick={() => setUseStockModalOpen(true)}
            >
              Stock Use
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="px-4 -mt-4 relative z-20">
        <Card className="shadow-xl border-none overflow-visible">
          <CardBody className="p-4 overflow-visible">
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Branch</label>
                <Select
                  options={branchOptions}
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e)}
                  isClearable
                  placeholder="All Branches"
                  className="text-sm"
                />
              </div>
              <Button
                color="primary"
                variant="flat"
                size="md"
                startContent={<MagnifyingGlassIcon className="h-4 w-4" />}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
              <Button
                color="default"
                variant="flat"
                size="md"
                startContent={<DocumentArrowDownIcon className="h-4 w-4" />}
                onClick={exportToExcel}
              >
                Export Excel
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Stock Summary Cards */}
      <div className="px-4 mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(stockSummary).map(([name, qty]) => (
            <Card key={name} className="shadow-md">
              <CardBody className="p-3 text-center">
                <p className="text-xs text-gray-500 uppercase">{name}</p>
                <p className={`text-xl font-bold ${Number(qty) < 0 ? "text-red-600" : "text-green-600"}`}>
                  {qty}
                </p>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>

      {/* Results Table */}
      <div className="px-4 mt-4 pb-8">
        <Card className="shadow-xl border-none overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 px-4 py-3">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {tableData.length} Records
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0 overflow-x-auto">
            {isLoading || isFetching ? (
              <div className="flex justify-center items-center py-20">
                <Spinner size="lg" />
              </div>
            ) : (
              <table ref={tableRef} className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Sr.</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Date {GET_SORT_ICON("createdAt")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Name {GET_SORT_ICON("name")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Description</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Category {GET_SORT_ICON("category")}
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-slate-700 border-b">
                      Qty {GET_SORT_ICON("qty")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Branch {GET_SORT_ICON("_subbranch.subbranch_name")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Bought By</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Note</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Added By</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-10 text-gray-500">
                        No stock records found
                      </td>
                    </tr>
                  ) : (
                    tableData.map((row, idx) => (
                      <tr key={row.id || idx} className="hover:bg-slate-50 border-b border-slate-100">
                        <td className="px-3 py-2 text-slate-600">{idx + 1}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.createdAt}
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">{row.name || "-"}</td>
                        <td className="px-3 py-2 text-slate-600">{row.desc || "-"}</td>
                        <td className="px-3 py-2">
                          <Chip size="sm" variant="flat" color="default">
                            {row.category || "-"}
                          </Chip>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`font-semibold ${
                              Number(row.qty) < 0 ? "text-red-600" : "text-green-600"
                            }`}
                          >
                            {Number(row.qty) > 0 ? "+" : ""}
                            {row.qty}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row._subbranch?.branch_name || "-"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row._boughtBy?.name || row._boughtBy?.username || "-"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.note === "transfer" ? (
                            <Chip size="sm" color="primary" variant="flat">
                              Transfer
                            </Chip>
                          ) : row.note === "used" ? (
                            <Chip size="sm" color="warning" variant="flat">
                              Used
                            </Chip>
                          ) : (
                            row.note || "-"
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row._addedBy?.name || row._addedBy?.username || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Add Stock Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setAddModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Add Stock</h3>
                <button onClick={() => setAddModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                  <Select
                    options={STOCK_ITEM_OPTIONS}
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e })}
                    placeholder="Select item..."
                  />
                </div>

                {addForm.name?.value === "other" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description / Custom Name *
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={addForm.desc}
                      onChange={(e) => setAddForm({ ...addForm, desc: e.target.value })}
                      placeholder="Enter item name/description"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <Select
                    options={CATEGORY_OPTIONS}
                    value={addForm.category}
                    onChange={(e) => setAddForm({ ...addForm, category: e })}
                    placeholder="Select category..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addForm.qty}
                    onChange={(e) => setAddForm({ ...addForm, qty: e.target.value })}
                    placeholder="Enter quantity"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
                  <Select
                    options={branchOptions}
                    value={addForm.subbranch}
                    onChange={(e) => setAddForm({ ...addForm, subbranch: e })}
                    placeholder="Select branch..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bought By</label>
                  <Select
                    options={userOptions}
                    value={addForm.boughtBy}
                    onChange={(e) => setAddForm({ ...addForm, boughtBy: e })}
                    placeholder="Select user..."
                    isClearable
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addForm.note}
                    onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
                    placeholder="Optional note"
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button color="default" variant="flat" onClick={() => setAddModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="success" onClick={handleAddStock} isLoading={isCreating}>
                  Add Stock
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Stock Modal */}
      {transferModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setTransferModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Transfer Stock</h3>
                <button onClick={() => setTransferModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                  <Select
                    options={STOCK_ITEM_OPTIONS.filter((o) => o.value !== "other")}
                    value={transferForm.name}
                    onChange={(e) => setTransferForm({ ...transferForm, name: e })}
                    placeholder="Select item..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={transferForm.qty}
                    onChange={(e) => setTransferForm({ ...transferForm, qty: e.target.value })}
                    placeholder="Enter quantity"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Branch *</label>
                  <Select
                    options={branchOptions}
                    value={transferForm.fromBranch}
                    onChange={(e) => setTransferForm({ ...transferForm, fromBranch: e })}
                    placeholder="Select source branch..."
                  />
                </div>

                <div className="flex items-center justify-center">
                  <ArrowsRightLeftIcon className="h-6 w-6 text-blue-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Branch *</label>
                  <Select
                    options={branchOptions}
                    value={transferForm.toBranch}
                    onChange={(e) => setTransferForm({ ...transferForm, toBranch: e })}
                    placeholder="Select destination branch..."
                  />
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 mt-4 text-sm text-blue-800">
                <p className="font-medium">This will create 2 entries:</p>
                <ul className="list-disc ml-5 mt-1">
                  <li>
                    -{transferForm.qty || "X"} from{" "}
                    <strong>{transferForm.fromBranch?.label || "source"}</strong>
                  </li>
                  <li>
                    +{transferForm.qty || "X"} to{" "}
                    <strong>{transferForm.toBranch?.label || "destination"}</strong>
                  </li>
                </ul>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button color="default" variant="flat" onClick={() => setTransferModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleTransferStock} isLoading={isTransferring}>
                  Transfer Stock
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Use Stock Modal */}
      {useStockModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setUseStockModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Stock Use</h3>
                <button onClick={() => setUseStockModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                  <Select
                    options={STOCK_ITEM_OPTIONS.filter((o) => o.value !== "other")}
                    value={useForm.name}
                    onChange={(e) => setUseForm({ ...useForm, name: e })}
                    placeholder="Select item..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity to Use *</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={useForm.qty}
                    onChange={(e) => setUseForm({ ...useForm, qty: e.target.value })}
                    placeholder="Enter quantity"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
                  <Select
                    options={branchOptions}
                    value={useForm.subbranch}
                    onChange={(e) => setUseForm({ ...useForm, subbranch: e })}
                    placeholder="Select branch..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={useForm.note}
                    onChange={(e) => setUseForm({ ...useForm, note: e.target.value })}
                    placeholder="Purpose of usage (optional)"
                    rows={2}
                  />
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 mt-4 text-sm text-yellow-800">
                <p>
                  This will <strong>subtract {useForm.qty || "X"}</strong> from{" "}
                  <strong>{useForm.subbranch?.label || "selected branch"}</strong>
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button color="default" variant="flat" onClick={() => setUseStockModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="warning" onClick={handleUseStock} isLoading={isUsingStock}>
                  Record Usage
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockReport;
